import { FlowSelectSourceType, WechatProfitSharingRelationTypeEnum, WechatProfitSharingTypeEnum } from '@lighthouse/core'
import type {
    AliyunMessageActionConfigure,
    BaiduSearchActionConfigure,
    BatchImportFileActionConfigure,
    BingSearchActionConfigure,
    CallActionConfigure,
    CopyActionConfigure,
    CreateRecordActionConfigure,
    CreateSingleRecordActionConfigure,
    DeleteRecordActionConfigure,
    DownloadFileActionConfigure,
    FindRecordActionConfigure,
    FindSingleRecordActionConfigure,
    GoogleSearchActionConfigure,
    HandleSubProcessActionConfigure,
    ImportExcelActionConfigure,
    LLMActionConfigure,
    LoopNodeActionConfigure,
    NotificationActionConfigure,
    OpenLinkActionConfigure,
    OpenPageActionConfigure,
    OpenWeChatCustomerServiceActionConfigure,
    ParamExtractionActionConfigure,
    RunCodeActionConfigure,
    SendEmailActionConfigure,
    SendMsgDingTalkRobotActionConfigure,
    SendMsgWxRobotActionConfigure,
    SendWebSocketActionConfigure,
    StationMessageActionConfigure,
    StripeActionConfigure,
    UpdateRecordActionConfigure,
    WeChatAddProfitSharingReceiverActionConfigure,
    WeChatDeleteProfitSharingReceiverActionConfigure,
    WechatFollowWaybillActionConfigure,
    WechatGenerateMiniProgramCodeActionConfigure,
    WechatGetOrderListActionConfigure,
    WechatGetOrderShippingActionConfigure,
    WechatNavigateToMiniProgramActionConfigure,
    WechatOpenChannelsActivityActionConfigure,
    WeChatOpenLocationActionConfigure,
    WeChatPayActionConfigure,
    WeChatProfitSharingActionConfigure,
    WeChatProfitSharingQueryActionConfigure,
    WeChatProfitSharingQueryAmountActionConfigure,
    WeChatRefundActionConfigure,
    WeChatRefundQueryActionConfigure,
    WechatTemplateMessageActionConfigure,
    WechatUploadShippingInfoActionConfigure
} from '@lighthouse/shared'
import {
    ActionDataNodeType,
    AINodeType,
    FlowControlNodeType,
    isEmptyRichTextValue,
    isEmptyVariableValue,
    isIntVariableValue,
    NoticeNodeType,
    OperatorNodeType,
    OtherPlatformNodeType
} from '@lighthouse/shared'

import type { ActionValidator, NodeValidator } from '../types'

/**
 * create record action 创建单条记录
 */
const createRecordActionValidator: NodeValidator<
    ActionValidator<CreateSingleRecordActionConfigure>,
    ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION
> = {
    nodeType: ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION,
    validators: [
        node => {
            return !!node.data.config?.dataSourceId
        }
    ]
}

// create multiRecord action
const createMultiRecordActionValidator: NodeValidator<
    ActionValidator<CreateRecordActionConfigure>,
    ActionDataNodeType.CREATE_RECORD_ACTION
> = {
    nodeType: ActionDataNodeType.CREATE_RECORD_ACTION,
    validators: [
        node => {
            return !!node.data.config?.dataSourceId && !!node.data.config?.nodeId
        }
    ]
}

// find record action
const findMultiRecordActionValidator: NodeValidator<ActionValidator<FindRecordActionConfigure>, ActionDataNodeType.FIND_RECORD_ACTION> = {
    nodeType: ActionDataNodeType.FIND_RECORD_ACTION,
    validators: [
        // dsId required
        node => {
            if (!node.data.config?.selectType) {
                return false
            }
            return node.data.config?.selectType === FlowSelectSourceType.DATASOURCE
                ? !!node.data.config?.dataSourceId
                : !!node.data.config?.nodeId
        }
    ]
}

// find record action
const findRecordActionValidator: NodeValidator<
    ActionValidator<FindSingleRecordActionConfigure>,
    ActionDataNodeType.FIND_SINGLE_RECORD_ACTION
> = {
    nodeType: ActionDataNodeType.FIND_SINGLE_RECORD_ACTION,
    validators: [
        // dsId required
        node => {
            if (!node.data.config?.selectType) {
                return false
            }
            return node.data.config?.selectType === FlowSelectSourceType.DATASOURCE
                ? !!node.data.config?.dataSourceId
                : !!node.data.config?.nodeId
        }
    ]
}

// update record action
const updateRecordActionValidator: NodeValidator<ActionValidator<UpdateRecordActionConfigure>, ActionDataNodeType.UPDATE_RECORD_ACTION> = {
    nodeType: ActionDataNodeType.UPDATE_RECORD_ACTION,
    validators: [
        // dsId required
        // node => {
        //     return !!node.data.config?.dataSourceId
        // },
        // 记录
        node => {
            if (!node.data.config?.selectType) {
                return false
            }
            if (node.data.config?.selectType === FlowSelectSourceType.UPSTREAM) {
                return !!node.data.config.nodeId
            }
            return !!node.data.config?.dataSourceId
        }
    ]
}

// delete record action
const deleteRecordActionValidator: NodeValidator<ActionValidator<DeleteRecordActionConfigure>, ActionDataNodeType.DELETE_RECORD> = {
    nodeType: ActionDataNodeType.DELETE_RECORD,
    validators: [
        // dsId required
        // node => {
        //     return !!node.data.config?.dataSourceId
        // },
        // 记录
        node => {
            if (!node.data.config?.selectType) {
                return false
            }
            if (node.data.config?.selectType === FlowSelectSourceType.UPSTREAM) {
                return !!node.data.config.nodeId
            }
            return !!node.data.config?.dataSourceId
        }
    ]
}

const importExcelActionValidator: NodeValidator<ActionValidator<ImportExcelActionConfigure>, ActionDataNodeType.IMPORT_EXCEL> = {
    nodeType: ActionDataNodeType.IMPORT_EXCEL,
    validators: [
        node => {
            return !!node.data.config?.dataSourceId
        },
        node => {
            return !!node.data.config?.excelSource && !isEmptyRichTextValue(node.data.config.excelSource)
        },
        node => {
            if (node.data.config.repeat) {
                return !!node.data.config.compareFields?.length && !!node.data.config.importMode
            }
            return true
        }
    ]
}

const batchImportFileActionValidator: NodeValidator<
    ActionValidator<BatchImportFileActionConfigure>,
    ActionDataNodeType.BATCH_IMPORT_FILE
> = {
    nodeType: ActionDataNodeType.BATCH_IMPORT_FILE,
    validators: [
        node => {
            return !!node.data.config?.dataSourceId
        },
        node => {
            return !!node.data.config?.attachmentFieldId
        },
        node => {
            return !!node.data.config?.mappingFieldId
        },
        node => {
            return !isEmptyVariableValue(node.data.config?.fileSource)
        }
    ]
}

// open page action
const openPageValidator: NodeValidator<ActionValidator<OpenPageActionConfigure>, OperatorNodeType.OPEN_PAGE> = {
    nodeType: OperatorNodeType.OPEN_PAGE,
    validators: [
        node => {
            return !!node.data.config?.openPageId
        }
    ]
}

// open link action
const openLinkValidator: NodeValidator<ActionValidator<OpenLinkActionConfigure>, OperatorNodeType.OPEN_LINK> = {
    nodeType: OperatorNodeType.OPEN_LINK,
    validators: [
        node => {
            return !!node.data.config?.link
        }
    ]
}

const openWeChatCustomerServiceValidator: NodeValidator<
    ActionValidator<OpenWeChatCustomerServiceActionConfigure>,
    OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE
> = {
    nodeType: OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE,
    validators: [
        node => {
            return !!node.data.config?.corpId && !isEmptyRichTextValue(node.data.config.corpId)
        },
        node => {
            return !!node.data.config?.url && !isEmptyRichTextValue(node.data.config.url)
        }
    ]
}

const notificationValidator: NodeValidator<ActionValidator<NotificationActionConfigure>, OperatorNodeType.NOTIFICATION> = {
    nodeType: OperatorNodeType.NOTIFICATION,
    validators: [
        node => {
            return !!node.data.config && !isEmptyRichTextValue(node.data.config.notificationContent)
        }
    ]
}

// copy action
const copyActionValidator: NodeValidator<ActionValidator<CopyActionConfigure>, OperatorNodeType.COPY> = {
    nodeType: OperatorNodeType.COPY,
    validators: [
        node => {
            return !!node.data.config && !isEmptyRichTextValue(node.data.config.copyText)
        }
    ]
}

// aliyunMessage action
const aliyunMessageActionValidator: NodeValidator<
    ActionValidator<AliyunMessageActionConfigure>,
    OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION
> = {
    nodeType: OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION,
    validators: [
        node => {
            return !!node.data.config?.signatureId && !!node.data.config?.templateId
        },
        node => {
            return !!node.data.config?.mobileList
        },
        node => {
            const { config } = node.data
            if (!config) {
                return false
            }
            const { values } = config
            return values?.length ? values.every(v => !isEmptyRichTextValue(v.value)) : true
        }
    ]
}

// aliyunMessage action
const wechatMessageActionValidator: NodeValidator<
    ActionValidator<WechatTemplateMessageActionConfigure>,
    OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION
> = {
    nodeType: OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION,
    validators: [
        node => {
            return !!node.data.config?.personList && node.data.config.personList.length > 0
        },
        node => {
            return !!node.data.config?.templateId
        },
        node => {
            const { config } = node.data
            if (!config) {
                return false
            }
            const { url } = config
            return !isEmptyRichTextValue(url)
        },
        node => {
            const { config } = node.data
            if (!config) {
                return false
            }
            const { values } = config
            return values?.length ? values.every(v => !isEmptyRichTextValue(v.value)) : true
        }
    ]
}

// dingtalkRobotAction
const dingtalkRobotActionValidator: NodeValidator<
    ActionValidator<SendMsgDingTalkRobotActionConfigure>,
    OtherPlatformNodeType.DINGTALK_ROBOT_ACTION
> = {
    nodeType: OtherPlatformNodeType.DINGTALK_ROBOT_ACTION,
    validators: [
        node => {
            return !!node.data.config?.msgtype && !!node.data.config?.webhook && !!node.data.config?.secret
        },
        node => {
            const msgtype = node.data.config?.msgtype

            if (!node.data?.config) {
                return false
            }

            const text = node.data?.config?.text
            const link = node.data?.config?.link
            const markdown = node.data?.config?.markdown

            if (msgtype === 'text') {
                return !!text && !!text?.editorContent && !isEmptyRichTextValue(text?.editorContent)
            } else if (msgtype === 'markdown') {
                return !!markdown && !!markdown?.editorText && !isEmptyRichTextValue(markdown?.editorText)
            }
            return (
                !!link &&
                !!link?.editorTitle &&
                !isEmptyRichTextValue(link?.editorTitle) &&
                !!link?.editorText &&
                !isEmptyRichTextValue(link?.editorText) &&
                !!link?.editorMessageUrl &&
                !isEmptyRichTextValue(link?.editorMessageUrl)
            )
        }
    ]
}

// wxRobotAction
const wxRobotActionValidator: NodeValidator<ActionValidator<SendMsgWxRobotActionConfigure>, OtherPlatformNodeType.WX_ROBOT_ACTION> = {
    nodeType: OtherPlatformNodeType.WX_ROBOT_ACTION,
    validators: [
        node => {
            return !!node.data.config?.msgtype && !!node.data.config?.webhook
        },
        node => {
            return !!node.data.config && !isEmptyRichTextValue(node.data.config?.text?.editorContent)
        }
    ]
}

// sendEmail action
const sendEmailActionValidator: NodeValidator<ActionValidator<SendEmailActionConfigure>, OtherPlatformNodeType.SEND_EMAIL_ACTION> = {
    nodeType: OtherPlatformNodeType.SEND_EMAIL_ACTION,
    validators: [
        node => {
            return !!node.data.config?.senderAccountType
        },
        node => {
            if (!node.data.config) {
                return false
            }
            const { recipients, cc, body, title } = node.data.config
            const { paragraph } = body || {}
            const isHasParagraph = paragraph?.length > 0
            // const isHasParagraph = paragraph?.some(p => {
            //     if(editType === EMAIL_EDIT_TYPE.HTML){
            //         return p.html && !isEmptyRichTextValue(p.html)
            //     }
            //     return p.richtext && !isEmptyRichTextValue(p.richtext)
            // })
            return !!recipients?.length && isHasParagraph && !isEmptyRichTextValue(title)
        }
    ]
}

// loop action
const loopActionValidator: NodeValidator<ActionValidator<LoopNodeActionConfigure>, FlowControlNodeType.LOOP_ACTION> = {
    nodeType: FlowControlNodeType.LOOP_ACTION,
    validators: [
        node => {
            return !!node.data.config?.loopType
        },
        node => {
            if (!node.data.config) {
                return false
            }
            const { loopType, loopErrorHandler } = node.data.config

            if (loopType === 'BASE_ON_RECORDS') {
                const { recordsConfig } = node.data.config

                return !!recordsConfig?.nodeId && !!loopErrorHandler && !!recordsConfig?.subProcessId
            }
            if (loopType === 'BASE_ON_TIMES') {
                const { timesConfig } = node.data.config
                return (
                    !!loopErrorHandler &&
                    !!timesConfig.subProcessId &&
                    isIntVariableValue(timesConfig?.start) &&
                    isIntVariableValue(timesConfig?.end) &&
                    isIntVariableValue(timesConfig?.step)
                )
            }
            return false
        }
    ]
}

// HANDLE_SUB_PROCESS
const handleSubProcessActionValidator: NodeValidator<
    ActionValidator<HandleSubProcessActionConfigure>,
    FlowControlNodeType.HANDLE_SUB_PROCESS
> = {
    nodeType: FlowControlNodeType.HANDLE_SUB_PROCESS,
    validators: [
        node => {
            return !!node.data.config.subProcessId
        }
    ]
}

// call action
const callActionValidator: NodeValidator<ActionValidator<CallActionConfigure>, OperatorNodeType.CALL> = {
    nodeType: OperatorNodeType.CALL,
    validators: [
        node => {
            return !isEmptyVariableValue(node.data.config?.phone)
        }
    ]
}

// download file action
const downloadFileActionValidator: NodeValidator<ActionValidator<DownloadFileActionConfigure>, OperatorNodeType.DOWNLOAD_FILE> = {
    nodeType: OperatorNodeType.DOWNLOAD_FILE,
    validators: [
        node => {
            return !!node.data.config?.fileUrl
        }
    ]
}

// wechatpay action
const wechatPayActionValidator: NodeValidator<ActionValidator<WeChatPayActionConfigure>, OtherPlatformNodeType.WECHAT_PAY> = {
    nodeType: OtherPlatformNodeType.WECHAT_PAY,
    validators: [
        node => {
            if (!node.data.config) {
                return false
            }
            const { config } = node.data
            return !!config?.descFieldId && !!config?.amountFieldId
        }
    ]
}

// stripe pay action
const stripePayActionValidator: NodeValidator<ActionValidator<StripeActionConfigure>, OtherPlatformNodeType.STRIPE> = {
    nodeType: OtherPlatformNodeType.STRIPE,
    validators: [
        node => {
            if (!node.data.config) {
                return false
            }
            const { config } = node.data
            return !!config?.currency && !!config?.amountFieldId && !!config.productNameFieldId && !!config.nodeId
        }
    ]
}

// ----------------------------
// 自动化独有
// ----------------------------

// sendStationMessage action
const stationMessageActionValidator: NodeValidator<
    ActionValidator<StationMessageActionConfigure>,
    NoticeNodeType.STATION_MESSAGE_ACTION
> = {
    nodeType: NoticeNodeType.STATION_MESSAGE_ACTION,
    validators: [
        node => {
            return (
                !!node.data.config &&
                !isEmptyRichTextValue(node.data.config.title) &&
                !isEmptyRichTextValue(node.data.config.content) &&
                !!node.data.config.addresseeIds?.length
            )
        }
    ]
}

// google search action
const googleSearchActionValidator: NodeValidator<
    ActionValidator<GoogleSearchActionConfigure>,
    OtherPlatformNodeType.GOOGLE_SEARCH_ACTION
> = {
    nodeType: OtherPlatformNodeType.GOOGLE_SEARCH_ACTION,
    validators: [
        node => {
            return !!node.data.config?.query && !isEmptyRichTextValue(node.data.config?.query)
        }
    ]
}

// baidu search action
const baiduSearchActionValidator: NodeValidator<ActionValidator<BaiduSearchActionConfigure>, OtherPlatformNodeType.BAIDU_SEARCH_ACTION> = {
    nodeType: OtherPlatformNodeType.BAIDU_SEARCH_ACTION,
    validators: [
        node => {
            return !!node.data.config?.query && !isEmptyRichTextValue(node.data.config?.query)
        }
    ]
}

// bing search action
const bingSearchActionValidator: NodeValidator<ActionValidator<BingSearchActionConfigure>, OtherPlatformNodeType.BING_SEARCH_ACTION> = {
    nodeType: OtherPlatformNodeType.BING_SEARCH_ACTION,
    validators: [
        node => {
            return !!node.data.config?.query && !isEmptyRichTextValue(node.data.config?.query)
        }
    ]
}

// wechat refund action
const WeChatRefundValidator: NodeValidator<ActionValidator<WeChatRefundActionConfigure>, OtherPlatformNodeType.WECHAT_REFUND> = {
    nodeType: OtherPlatformNodeType.WECHAT_REFUND,
    validators: [
        // 验证SourceRecordConfig
        node => {
            if (!node.data.config?.selectType) {
                return false
            }
            if (node.data.config?.selectType === FlowSelectSourceType.UPSTREAM) {
                return !!node.data.config.nodeId
            }
            return !!node.data.config?.dataSourceId
        },
        // 验证退款相关字段
        node => {
            return !!node.data.config?.payOrderFieldId && !!node.data.config?.payAmountFieldId && !!node.data.config?.refundAmountFieldId
        }
    ]
}

// wechat refund query action
const WeChatRefundQueryValidator: NodeValidator<
    ActionValidator<WeChatRefundQueryActionConfigure>,
    OtherPlatformNodeType.WECHAT_REFUND_QUERY
> = {
    nodeType: OtherPlatformNodeType.WECHAT_REFUND_QUERY,
    validators: [
        node => {
            return !!node.data.config?.refundOrderId && !isEmptyRichTextValue(node.data.config.refundOrderId)
        }
    ]
}

// wechat profit sharing action
const WeChatProfitSharingValidator: NodeValidator<
    ActionValidator<WeChatProfitSharingActionConfigure>,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING
> = {
    nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING,
    validators: [
        // 验证RichText字段
        node => {
            return (
                !!node.data.config?.outTradeNo &&
                !isEmptyRichTextValue(node.data.config.outTradeNo) &&
                !!node.data.config?.profitSharingOrderId &&
                !isEmptyRichTextValue(node.data.config.profitSharingOrderId) &&
                !!node.data.config?.isUnfreezeRemainingFund &&
                !isEmptyRichTextValue(node.data.config.isUnfreezeRemainingFund)
            )
        },
        // 验证profitSharingReceiver对象
        node => {
            const receiver = node.data.config?.profitSharingReceiver
            if (!receiver) {
                return false
            }
            return (
                !!receiver.nodeId &&
                !!receiver.profitSharingAccount &&
                !isEmptyRichTextValue(receiver.profitSharingAccount) &&
                !!receiver.profitSharingType &&
                !!receiver.profitSharingAmount &&
                !isEmptyRichTextValue(receiver.profitSharingAmount) &&
                !!receiver.profitSharingDescription &&
                !isEmptyRichTextValue(receiver.profitSharingDescription)
            )
        },
        // 条件验证：当分账类型为个人时，平台字段必填
        node => {
            const receiver = node.data.config?.profitSharingReceiver
            if (!receiver) {
                return false
            }
            if (receiver.profitSharingType === WechatProfitSharingTypeEnum.PERSONAL_OPENID) {
                return !!receiver.profitSharingPlatform
            }
            return true
        }
    ]
}

// wechat profit sharing query action
const WeChatProfitSharingQueryValidator: NodeValidator<
    ActionValidator<WeChatProfitSharingQueryActionConfigure>,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY
> = {
    nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY,
    validators: [
        node => {
            return (
                !!node.data.config?.profitSharingOrderId &&
                !isEmptyRichTextValue(node.data.config.profitSharingOrderId) &&
                !!node.data.config?.outTradeNo &&
                !isEmptyRichTextValue(node.data.config.outTradeNo)
            )
        }
    ]
}

// wechat profit sharing query amount action
const WeChatProfitSharingQueryAmountValidator: NodeValidator<
    ActionValidator<WeChatProfitSharingQueryAmountActionConfigure>,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT
> = {
    nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT,
    validators: [
        node => {
            return !!node.data.config?.outTradeNo && !isEmptyRichTextValue(node.data.config.outTradeNo)
        }
    ]
}

const WeChatAddProfitSharingReceiverValidator: NodeValidator<
    ActionValidator<WeChatAddProfitSharingReceiverActionConfigure>,
    OtherPlatformNodeType.WECHAT_ADD_RECEIVER
> = {
    nodeType: OtherPlatformNodeType.WECHAT_ADD_RECEIVER,
    validators: [
        node => {
            return (
                !!node.data.config.type &&
                !!node.data.config.account &&
                !isEmptyRichTextValue(node.data.config.account) &&
                !!node.data.config.relationType
            )
        },
        node => {
            if (node.data.config.type === WechatProfitSharingTypeEnum.PERSONAL_OPENID && !node.data.config.platform) {
                return false
            }
            if (
                node.data.config.type === WechatProfitSharingTypeEnum.MERCHANT_ID &&
                (!node.data.config.name || isEmptyRichTextValue(node.data.config.name))
            ) {
                return false
            }
            if (node.data.config.relationType === WechatProfitSharingRelationTypeEnum.CUSTOM) {
                return !!node.data.config.customRelation && !isEmptyRichTextValue(node.data.config.customRelation)
            }
            return true
        }
    ]
}

const WeChatDeleteProfitSharingReceiverValidator: NodeValidator<
    ActionValidator<WeChatDeleteProfitSharingReceiverActionConfigure>,
    OtherPlatformNodeType.WECHAT_DELETE_RECEIVER
> = {
    nodeType: OtherPlatformNodeType.WECHAT_DELETE_RECEIVER,
    validators: [
        node => {
            return !!node.data.config.type && !!node.data.config.account && !isEmptyRichTextValue(node.data.config.account)
        },
        node => {
            if (node.data.config.type === WechatProfitSharingTypeEnum.PERSONAL_OPENID) {
                return !!node.data.config.platform
            }
            return true
        }
    ]
}
// AI
const llmActionActionValidator: NodeValidator<ActionValidator<LLMActionConfigure>, AINodeType.LLM_ACTION> = {
    nodeType: AINodeType.LLM_ACTION,
    validators: [
        node => {
            return !!node.data.config.model && !isEmptyRichTextValue(node.data.config?.userPrompt)
        }
    ]
}

const paramExtractActionActionValidator: NodeValidator<ActionValidator<ParamExtractionActionConfigure>, AINodeType.PARAM_EXTRACT_ACTION> = {
    nodeType: AINodeType.PARAM_EXTRACT_ACTION,
    validators: [
        node => {
            return !!node.data.config.model
        }
    ]
}

const runCodeActionActionValidator: NodeValidator<ActionValidator<RunCodeActionConfigure>, AINodeType.CODE_ACTION> = {
    nodeType: AINodeType.CODE_ACTION,
    validators: [
        node => {
            return !!node.data.config.code && node.data.config.args && node.data.config.args.length > 0
        }
    ]
}

const WechatOpenLocationActionValidator: NodeValidator<
    ActionValidator<WeChatOpenLocationActionConfigure>,
    OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION
> = {
    nodeType: OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION,
    validators: [
        node => {
            return (
                !!node.data.config?.latitude &&
                !isEmptyRichTextValue(node.data.config?.latitude) &&
                !!node.data.config?.longitude &&
                !isEmptyRichTextValue(node.data.config?.longitude)
            )
        }
    ]
}

const WechatNavigateToMiniProgramActionValidator: NodeValidator<
    ActionValidator<WechatNavigateToMiniProgramActionConfigure>,
    OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM
> = {
    nodeType: OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM,
    validators: [
        node => {
            return !!node.data.config?.appId && !isEmptyRichTextValue(node.data.config?.appId)
        }
    ]
}

const WechatOpenChannelsActivityActionValidator: NodeValidator<
    ActionValidator<WechatOpenChannelsActivityActionConfigure>,
    OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY
> = {
    nodeType: OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY,
    validators: [
        node => {
            return (
                !!node.data.config?.finderUserName &&
                !isEmptyRichTextValue(node.data.config?.finderUserName) &&
                !!node.data.config?.feedId &&
                !isEmptyRichTextValue(node.data.config?.feedId)
            )
        }
    ]
}

const WechatGenerateMiniProgramCodeActionValidator: NodeValidator<
    ActionValidator<WechatGenerateMiniProgramCodeActionConfigure>,
    OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE
> = {
    nodeType: OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE,
    validators: [
        node => {
            return !!node.data.config?.path && !isEmptyRichTextValue(node.data.config.path)
        },
        node => {
            return !!node.data.config?.scene && !isEmptyRichTextValue(node.data.config.scene)
        }
    ]
}

const WechatUploadShippingInfoActionValidator: NodeValidator<
    ActionValidator<WechatUploadShippingInfoActionConfigure>,
    OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO
> = {
    nodeType: OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO,
    validators: [
        node => !!node.data.config?.orderNumberType && !!node.data.config?.logisticsType && !!node.data.config?.deliveryMode,
        node => {
            const config = node.data.config
            if (config?.orderNumberType === 1) {
                return !!config.outTradeNo && !isEmptyRichTextValue(config.outTradeNo)
            }
            return !!config?.transactionId && !isEmptyRichTextValue(config.transactionId)
        },
        node => !!node.data.config?.itemDesc && !isEmptyRichTextValue(node.data.config.itemDesc),
        node => {
            const config = node.data.config
            if (config?.logisticsType !== 1) {
                return true
            }
            return (
                !!config.trackingNo &&
                !isEmptyRichTextValue(config.trackingNo) &&
                !!config.expressCompany &&
                !isEmptyRichTextValue(config.expressCompany)
            )
        },
        node => {
            const config = node.data.config
            if (config?.deliveryMode !== 2) {
                return true
            }
            return !!config.isAllDelivered && !isEmptyRichTextValue(config.isAllDelivered)
        }
    ]
}

const WechatGetOrderShippingActionValidator: NodeValidator<
    ActionValidator<WechatGetOrderShippingActionConfigure>,
    OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING
> = {
    nodeType: OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING,
    validators: [
        node => !!node.data.config?.orderNumberType,
        node => {
            const config = node.data.config
            if (config?.orderNumberType === 1) {
                return !!config.merchantTradeNo && !isEmptyRichTextValue(config.merchantTradeNo)
            }
            return !!config?.transactionId && !isEmptyRichTextValue(config.transactionId)
        }
    ]
}

const WechatGetOrderListActionValidator: NodeValidator<
    ActionValidator<WechatGetOrderListActionConfigure>,
    OtherPlatformNodeType.WECHAT_GET_ORDER_LIST
> = {
    nodeType: OtherPlatformNodeType.WECHAT_GET_ORDER_LIST,
    validators: [
        node => !!node.data.config?.openid && !isEmptyRichTextValue(node.data.config.openid)
    ]
}

const WechatFollowWaybillActionValidator: NodeValidator<
    ActionValidator<WechatFollowWaybillActionConfigure>,
    OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL
> = {
    nodeType: OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL,
    validators: [
        node => {
            const config = node.data.config
            return (
                !!config?.openid &&
                !isEmptyRichTextValue(config.openid) &&
                !!config.waybillId &&
                !isEmptyRichTextValue(config.waybillId) &&
                !!config.transId &&
                !isEmptyRichTextValue(config.transId) &&
                !!config.receiverPhone &&
                !isEmptyRichTextValue(config.receiverPhone) &&
                !!config.orderDetailPath &&
                !isEmptyRichTextValue(config.orderDetailPath) &&
                !!config.goodsName &&
                !isEmptyRichTextValue(config.goodsName) &&
                !!config.goodsImgUrl &&
                !isEmptyRichTextValue(config.goodsImgUrl)
            )
        }
    ]
}

const sendWebSocketActionValidator: NodeValidator<ActionValidator<SendWebSocketActionConfigure>, FlowControlNodeType.SEND_WEBSOCKET> = {
    nodeType: FlowControlNodeType.SEND_WEBSOCKET,
    validators: [
        node => {
            return !!node.data.config?.eventName && !!node.data.config?.eventValue
        }
    ]
}

export const actionValidators = [
    createRecordActionValidator,
    createMultiRecordActionValidator,
    findRecordActionValidator,
    findMultiRecordActionValidator,
    updateRecordActionValidator,
    deleteRecordActionValidator,
    importExcelActionValidator,
    batchImportFileActionValidator,
    openPageValidator,
    openLinkValidator,
    openWeChatCustomerServiceValidator,
    notificationValidator,
    copyActionValidator,
    aliyunMessageActionValidator,
    wechatMessageActionValidator,
    dingtalkRobotActionValidator,
    wxRobotActionValidator,
    sendEmailActionValidator,
    loopActionValidator,
    handleSubProcessActionValidator,
    WechatOpenLocationActionValidator,
    WechatNavigateToMiniProgramActionValidator,
    WechatOpenChannelsActivityActionValidator,
    WechatGenerateMiniProgramCodeActionValidator,
    WechatUploadShippingInfoActionValidator,
    WechatGetOrderShippingActionValidator,
    WechatGetOrderListActionValidator,
    WechatFollowWaybillActionValidator,

    // 动作流独有
    callActionValidator,
    downloadFileActionValidator,
    wechatPayActionValidator,
    // 自动化独有
    stationMessageActionValidator,
    stripePayActionValidator,

    googleSearchActionValidator,
    baiduSearchActionValidator,
    bingSearchActionValidator,

    WeChatRefundValidator,
    WeChatRefundQueryValidator,
    WeChatProfitSharingValidator,
    WeChatProfitSharingQueryValidator,
    WeChatProfitSharingQueryAmountValidator,
    WeChatAddProfitSharingReceiverValidator,
    WeChatDeleteProfitSharingReceiverValidator,
    sendWebSocketActionValidator,

    // AI
    llmActionActionValidator,
    paramExtractActionActionValidator,
    runCodeActionActionValidator
]
